import type {
  StaticDataTypeAllowedSharedProfileTabs,
  StaticDataTypeOwnDataProfileTabs,
} from "./_generated/static-data-types";
import {
  isStaticDataTypeOwnDataProfileTabs,
  StaticDataTypeOwnDataProfileTabsValues,
} from "./_generated/static-data-types";
import type { TabPermission } from "./candidate-things";
import type { nil } from "./type-utils";
import { isNil } from "lodash";

/**
 * The tabs of an internal (employee) mask that govern a user's OWN data: what the user's organization holds on a candidate of another
 * organization it has a licence for (own internal fields, notes, interviews, accounting records).
 *
 * Configured apart from the owner's tabs on purpose: the owner's data follows sharing AND mask, the own data the mask alone, since a
 * sharing may not restrict what an organization keeps about a candidate itself. Kept out of `AllowedSharedProfileTabs`, so only a `User`
 * mask can carry them, no sharing, template or preset. Their keys, labels and translations are the static data `OwnDataProfileTabs`.
 */
export type OwnDataTabKey = StaticDataTypeOwnDataProfileTabs;

export const OWN_DATA_TAB_KEYS = StaticDataTypeOwnDataProfileTabsValues;

/**
 * The owner's tab each own-data tab mirrors: it holds the same fields, at the paths of the licensed copy
 * (`licensedOrganizationData.<owner path>`), or `null` if there is no counterpart. A new own-data tab needs an entry in the static data
 * `OwnDataProfileTabs` and one here.
 */
export const OWNER_TAB_OF_OWN_DATA_TAB: Record<OwnDataTabKey, StaticDataTypeAllowedSharedProfileTabs | null> = {
  ownInternal: "internal",
  ownNotes: "notes",
  ownInterviews: "interviews",
  ownAccountingRecords: "accountingRecords",
};

/** Every key a mask entry can carry: the shared profile tabs and the user's own-data tabs. */
export type MaskTabKey = StaticDataTypeAllowedSharedProfileTabs | OwnDataTabKey;

export function isOwnDataTabKey(key: string | nil): key is OwnDataTabKey {
  return isStaticDataTypeOwnDataProfileTabs(key);
}

/**
 * Where an organization keeps its OWN data on a candidate of another organization it licenses: beside the owner's data, the same fields at
 * the same paths below this root (`licensedOrganizationData.internal.costCenter` mirrors `internal.costCenter`). The catalog guard
 * (`candidate-tab-mapping.validator.ts`) skips it: own data is governed by the own-data tabs, not by a `profileTab` of its own.
 */
export const OWN_DATA_ROOT_PATH = "licensedOrganizationData";

const OWN_DATA_PREFIX = `${OWN_DATA_ROOT_PATH}.`;

/** The Candidate path of the own copy of an owner's field (`internal.costCenter` -> `licensedOrganizationData.internal.costCenter`), the identity of an own-data tab entry. */
export function toOwnDataPath(ownerPath: string): string {
  return `${OWN_DATA_PREFIX}${ownerPath}`;
}

/** The inverse of {@link toOwnDataPath}. A path that is no own copy is returned as it is. */
export function toOwnerPath(path: string): string {
  return isOwnDataPath(path) ? path.slice(OWN_DATA_PREFIX.length) : path;
}

export function isOwnDataPath(path: string | nil): boolean {
  return path?.startsWith(OWN_DATA_PREFIX) ?? false;
}

/** What this module needs of a mask's `tabs` and a `SharedTab`, structurally typed so backend classes and the frontend's generated fragments (a different `TabPermission` enum, same values) both satisfy it. */
export interface MaskTabLike<TPermission = TabPermission> {
  key: string;
  fullTabPermission?: TPermission | nil;
  tabFields?: { name: string; isEnabled?: boolean | nil; isMandatory?: boolean | nil }[] | nil;
}

export interface SharedTabLike<TPermission = TabPermission> {
  key: string;
  fullTabPermission?: TPermission | nil;
  sharedFields?: { name: string; isEnabled?: boolean | nil; isMandatory?: boolean | nil }[] | nil;
}

/**
 * A mask's `tabs` as the `SharedTab[]` the sharing machinery understands (`respectSharedTabsAndFields`, `FieldVisibilityService`), so a mask
 * is applied with the same primitives as a sharing. `fullTabPermission` wins (`sharedFields` omitted, `tabFields` ignored); non-empty
 * `tabFields` is field-level detail, carried over 1:1; a tab with neither is "not shared" and omitted, which `respectSharedTabsAndFields`
 * treats as fully restricted.
 *
 * That includes `tabFields == null`, which used to read as "whole tab, unrestricted". Saving strips empty arrays (`removeAllNullValues`),
 * so a tab stored with NOTHING selected comes back without `tabFields`, and `develop` reads it as not configured: opening it would expose
 * every such tab of an existing mask after the rollout. See `isSharedTab`.
 */
export function toSharedTabsOfMask<TPermission>(
  tabs: readonly MaskTabLike<TPermission>[] | nil
): SharedTabLike<TPermission>[] {
  return (tabs ?? [])
    .filter((tab) => !isNil(tab.fullTabPermission) || (tab.tabFields?.length ?? 0) > 0)
    .map((tab) => {
      if (!isNil(tab.fullTabPermission)) {
        return { key: tab.key, fullTabPermission: tab.fullTabPermission };
      }

      return {
        key: tab.key,
        sharedFields: tab.tabFields.map(({ name, isEnabled, isMandatory }) => ({ name, isEnabled, isMandatory })),
      };
    });
}

/** `tabs` apart: the owner's tabs, and the tabs of the user's own data ({@link OWN_DATA_TAB_KEYS}). */
export function splitOwnDataTabs<TTab extends { key: string }>(
  tabs: readonly TTab[] | nil
): { ownerTabs: TTab[]; ownDataTabs: TTab[] } {
  const all = tabs ?? [];
  return {
    ownerTabs: all.filter((tab) => !isOwnDataTabKey(tab.key)),
    ownDataTabs: all.filter((tab) => isOwnDataTabKey(tab.key)),
  };
}

/**
 * An own-data tab as the owner's tab it mirrors (names back to the owner's paths), which the catalog and `respectSharedTabsAndFields` know,
 * so own data is restricted with the same code as a candidate's. `null` for a tab that is no own-data tab or has no owner tab.
 */
export function toOwnerTab<TTab extends SharedTabLike<unknown>>(tab: TTab): TTab | null {
  const ownerTab = isOwnDataTabKey(tab.key) ? OWNER_TAB_OF_OWN_DATA_TAB[tab.key] : null;
  if (isNil(ownerTab)) {
    return null;
  }

  return {
    ...tab,
    key: ownerTab,
    sharedFields: tab.sharedFields?.map((field) => ({ ...field, name: toOwnerPath(field.name) })),
  };
}

/** {@link toOwnerTab} for a list; no own-data tab means no owner tab: nothing of that data may be changed. */
export function toOwnerTabsOfOwnData<TTab extends SharedTabLike<unknown>>(ownDataTabs: readonly TTab[] | nil): TTab[] {
  return (ownDataTabs ?? []).map(toOwnerTab).filter((tab): tab is TTab => !isNil(tab));
}

/** The inverse of {@link toOwnerTab} for one tab, e.g. after the owner's tab was closed over its dependencies. */
export function toOwnDataTab<TTab extends SharedTabLike<unknown>>(ownTabKey: OwnDataTabKey, ownerTab: TTab): TTab {
  return {
    ...ownerTab,
    key: ownTabKey,
    sharedFields: ownerTab.sharedFields?.map((field) => ({ ...field, name: toOwnDataPath(field.name) })),
  };
}
